(() => {
  const labels = document.currentScript.dataset;
  const requests = new Map();
  const getProduct = (id, title) => {
    if (!requests.has(id)) {
      const url = new URL(`${labels.searchUrl}`, location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', `title:"${title.replaceAll('"', '')}"`);
      url.searchParams.set('view', 'clarity-prices');
      const request = fetch(url, { credentials: 'same-origin' })
        .then(response => { if (!response.ok) throw new Error('Price lookup failed'); return response.json(); })
        .then(products => products.find(product => String(product.id) === id))
        .catch(() => null);
      requests.set(id, request);
    }
    return requests.get(id);
  };
  let tries = 0;
  const bind = () => {
    const root = document.getElementById('ads-agent-host')?.shadowRoot;
    if (!root) { if (++tries < 120) setTimeout(bind, 250); return; }
    const style = document.createElement('style');
    style.textContent = `
      #root [data-gruendl-product-card] button[class~="bg-background-1-contrast"],
      #root button[aria-label="Warenkorb"], #root button[aria-label="Cart"],
      #root button[data-gruendl-mutation] { display: none !important; }
      #root .gruendl-product-link {
        display: flex; align-items: center; justify-content: center;
        min-height: 44px; padding: 10px 18px; border-radius: 999px;
        background: #008653; color: #fff; text-decoration: none;
        font: 600 15px/1.4 var(--gruendl-chat-font, system-ui, sans-serif);
      }
      #root .gruendl-product-link:hover { background: #007547; }
      #root .gruendl-product-link:focus-visible { outline: 2px solid #007547; outline-offset: 3px; }
      #root .gruendl-card-note { color: #006e45; font-size: 14px; line-height: 1.4; margin: 6px 0; }
    `;
    root.append(style);
    const mutationLabel = /^(?:add to cart|in den warenkorb(?: legen)?|zum warenkorb hinzufügen|warenkorb|cart|checkout|zur kasse)$/i;
    root.addEventListener('click', event => {
      const button = event.target.closest?.('button');
      if (button && (button.hasAttribute('data-gruendl-mutation') || mutationLabel.test(button.getAttribute('aria-label') || button.textContent.trim()))) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
    const results = new Map();
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (!scheduled) { scheduled = true; requestAnimationFrame(update); }
    });
    const update = () => {
      scheduled = false;
      observer.disconnect();
      root.querySelectorAll('button[data-component-type="SUGGESTION_CHIP"]').forEach(button => {
        if (mutationLabel.test(button.textContent.trim())) button.setAttribute('data-gruendl-mutation', '');
      });
      root.querySelectorAll('[id^="id-"]').forEach(card => {
        const id = card.id.slice(3);
        const heading = card.querySelector('h3');
        if (!heading) return;
        card.setAttribute('data-gruendl-product-card', '');
        const title = heading.textContent.trim();
        if (/^Anleitung\b/i.test(title) && !card.querySelector('.gruendl-card-note')) {
          const note = document.createElement('p');
          note.className = 'gruendl-card-note';
          note.textContent = labels.instructionOnly;
          heading.after(note);
        }
        const buyButton = card.querySelector('button[class~="bg-background-1-contrast"]');
        if (buyButton) {
          buyButton.disabled = true;
          buyButton.setAttribute('data-gruendl-mutation', '');
          if (!card.querySelector('.gruendl-product-link')) {
            const link = document.createElement('a');
            link.className = 'gruendl-product-link';
            const search = new URL(labels.searchUrl, location.origin);
            search.searchParams.set('q', title);
            search.searchParams.set('type', 'product');
            link.href = search.href;
            link.textContent = labels.viewProduct;
            buyButton.after(link);
          }
        }
        if (!results.has(id)) {
          results.set(id, null);
          getProduct(id, title).then(product => { results.set(id, product || false); update(); });
        }
        const product = results.get(id);
        const link = card.querySelector('.gruendl-product-link');
        if (product && link) {
          const url = new URL(product.url, location.origin);
          if (url.origin === location.origin) link.href = url.href;
        }
        const price = Array.from(card.querySelectorAll('span.sr-only')).find(el => /^(Aktueller Preis|Current price):/.test(el.textContent));
        const visiblePrice = price?.parentElement.querySelector('p[aria-hidden="true"]');
        if (!price || !visiblePrice) return;
        const zero = /^(?:[A-Z]{3}\s*)?0[,.]00\s*[€$£]?$/.test(visiblePrice.textContent.replace(/\u00a0/g, ' ').trim());
        const freePattern = product && !product.is_set && product.minimum === 0 && /^Anleitung\b/i.test(title);
        const display = product?.is_set || freePattern ? product.price_text : zero ? labels.checkPrice : null;
        if (display) {
          const prefix = price.textContent.split(':')[0];
          if (visiblePrice.textContent !== display) visiblePrice.textContent = display;
          const accessible = `${prefix}: ${display}`;
          if (price.textContent !== accessible) price.textContent = accessible;
        }
      });
      observer.observe(root, { childList: true, characterData: true, subtree: true });
    };
    update();
  };
  bind();
})();

// Explain only information already returned by Clarity's native verified order card.
// This does not query orders, retain customer details, or change the AI's model.
(() => {
  if (!/^de(?:-|$)/i.test(document.documentElement.lang)) return;
  const cardSelector = '[data-component-type="ORDER_TRACKING_CARD"]';
  const fallback = /^Ich kann (?:den Status deiner Bestellung|den Versandstatus oder eine Sendungsnummer f[üu]r deine Bestellung)\s+([#\w-]+)\s+leider nicht direkt abrufen\./i;
  const normalize = value => value.replace(/^#/, '').toUpperCase();
  const summary = card => {
    const status = card.querySelector('h2')?.textContent.trim();
    const number = card.querySelector('span')?.textContent.match(/^Bestellung\s+([#\w-]+)$/i)?.[1];
    if (!status || !number) return null;
    const tracking = [...card.querySelectorAll('a[href]')].some(link =>
      /sendung|tracking|verfolg/i.test(link.textContent + ' ' + (link.getAttribute('aria-label') || ''))
    );
    const parts = [`Die Bestellübersicht zeigt „${status}“.`];
    if (/^Bestellung aufgegeben$/i.test(status)) {
      parts.push('Deine Bestellung ist eingegangen. In dieser Übersicht ist noch kein Versand bestätigt.');
    }
    parts.push(tracking
      ? 'Nutze die Sendungsverfolgung in dieser Bestellübersicht für weitere Versanddetails.'
      : 'Hier wird derzeit keine Sendungsnummer angezeigt.');
    return { number, status, explanation: parts.join(' '), details: parts.slice(1).join(' ') };
  };
  let attempts = 0;
  const bind = () => {
    const root = document.getElementById('ads-agent-host')?.shadowRoot;
    if (!root) { if (++attempts < 120) setTimeout(bind, 250); return; }
    if (root.querySelector('[data-gruendl-order-help-style]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-gruendl-order-help-style', '');
    style.textContent = `
      #root .gruendl-order-guide { padding: 12px; border: 1px solid #c8e3d5; border-radius: 12px; background: #f3f9f5; color: #244636; font-size: 14px; line-height: 1.5; }
      #root .gruendl-order-guide strong { display: block; margin-bottom: 4px; color: #006e45; }
      #root .gruendl-order-guide p { margin: 0; }
      #root .gruendl-order-reply { padding: 8px; }
      #root [data-gruendl-order-fallback] { display: none !important; }
    `;
    root.append(style);
    const ownGuide = (parent, className) => [...parent.children].find(child => child.classList.contains(className));
    const setGuide = (parent, className, title, text) => {
      let guide = ownGuide(parent, className);
      if (!guide) {
        guide = document.createElement('div');
        guide.className = className;
        const heading = document.createElement('strong');
        const paragraph = document.createElement('p');
        guide.append(heading, paragraph);
        parent.append(guide);
      }
      if (guide.firstChild.textContent !== title) guide.firstChild.textContent = title;
      if (guide.lastChild.textContent !== text) guide.lastChild.textContent = text;
    };
    let queued = false;
    const observer = new MutationObserver(() => {
      if (!queued) { queued = true; requestAnimationFrame(update); }
    });
    const update = () => {
      queued = false;
      observer.disconnect();
      root.querySelectorAll(cardSelector).forEach(card => {
        const data = summary(card);
        if (data) setGuide(card, 'gruendl-order-guide', 'Was bedeutet dieser Status?', data.explanation);
        else ownGuide(card, 'gruendl-order-guide')?.remove();
      });
      // Keep model text intact in the DOM; the theme supplies this narrowly scoped
      // form introduction instead of the contradictory "cannot retrieve" fallback.
      root.querySelectorAll('[data-testid^="agent-message-"]').forEach(message => {
        const article = message.closest('[role="article"]');
        const next = article?.nextElementSibling;
        const paragraphs = [...message.querySelectorAll('p')];
        const match = paragraphs.length === 1 && paragraphs[0].textContent.match(fallback);
        const form = next?.querySelector('input[type="email"]') ? next : null;
        if (!match || !form) {
          message.querySelector('[data-gruendl-order-fallback]')?.removeAttribute('data-gruendl-order-fallback');
          if (article) ownGuide(article, 'gruendl-order-reply')?.remove();
          return;
        }
        let previous = article.previousElementSibling;
        let found = null;
        let blocked = /Wir konnten keine Bestellung finden/i.test(form.textContent);
        while (previous && !found && !blocked) {
          if (/Wir konnten keine Bestellung finden/i.test(previous.textContent)) blocked = true;
          const card = previous.querySelector(cardSelector);
          if (card) found = summary(card);
          previous = previous.previousElementSibling;
        }
        const sameOrder = !blocked && found && normalize(found.number) === normalize(match[1]);
        const text = sameOrder
          ? `Zuletzt abgerufener Status für Bestellung ${found.number}: „${found.status}“. ${found.details} Für einen neuen Abruf bestätige deine Angaben im Formular.`
          : 'Prüfe deinen Bestellstatus mit der Bestellnummer und der E-Mail-Adresse aus deiner Bestellung. Bestätige die Angaben im Formular; danach erscheint deine Bestellübersicht.';
        paragraphs[0].setAttribute('data-gruendl-order-fallback', '');
        setGuide(article, 'gruendl-order-reply', 'Bestellauskunft', text);
      });
      observer.observe(root, { childList: true, characterData: true, subtree: true });
    };
    update();
  };
  bind();
})();
